import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SIDEBAR_ITEMS } from "constants/navigation";
import routes from "constants/routes";
import { ReactComponent as Logo } from "shared/Icons/logo.svg";
import { tokenUtils } from "utils";

import styles from "./DashboardLayout.module.scss";

const getPageTitle = (pathname) => {
  if (pathname === routes.uploads.root) return "Uploads";
  if (pathname === routes.coupons.create) return "Create Coupon";
  if (pathname.match(/\/coupons\/[^/]+\/edit$/)) return "Edit Coupon";
  if (pathname.match(/\/coupons\/[^/]+$/)) return "View Coupon";
  if (pathname === routes.coupons.list) return "Coupons";
  if (pathname === routes.packages.root) return "Packages";
  return "Dashboard";
};

const getPageSubtitle = (pathname) => {
  if (pathname === routes.coupons.list) {
    return "Manage and track all your coupons.";
  }
  return null;
};

const getCouponBreadcrumb = (pathname) => {
  if (pathname === routes.coupons.create) {
    return { show: true, label: "Create Coupon" };
  }
  const editMatch = pathname.match(/^\/coupons\/([^/]+)\/edit$/);
  if (editMatch) {
    return { show: true, label: decodeURIComponent(editMatch[1]) };
  }
  const viewMatch = pathname.match(/^\/coupons\/([^/]+)$/);
  if (viewMatch) {
    return { show: true, label: decodeURIComponent(viewMatch[1]) };
  }
  return { show: false, label: "" };
};

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = tokenUtils.getDisplayName();
  const userInitials = tokenUtils.getUserInitials();
  const pageTitle = getPageTitle(location.pathname);
  const pageSubtitle = getPageSubtitle(location.pathname);
  const couponBreadcrumb = getCouponBreadcrumb(location.pathname);

  useEffect(() => {
    tokenUtils.syncUserProfileFromToken(tokenUtils.getToken());
  }, []);

  const handleLogout = () => {
    tokenUtils.deleteToken();
    navigate("/login");
  };

  return (
    <div className={styles.shell}>
      <div
        className={`${styles.topBar} ${
          couponBreadcrumb.show ? styles.topBarWithBreadcrumb : ""
        }`}
      >
        <div className={styles.logoPanel}>
          <button
            type="button"
            className={styles.logoButton}
            onClick={() => navigate(routes.coupons.list)}
            aria-label="Go to coupons"
          >
            <Logo className={styles.logo} aria-hidden="true" />
          </button>
        </div>

        <header
          className={`${styles.headerBar} ${
            couponBreadcrumb.show ? styles.headerBarWithBreadcrumb : ""
          }`}
        >
          <div className={styles.headerLeft}>
            <button type="button" className={styles.menuButton} aria-label="Menu">
              <MenuOutlinedIcon />
            </button>
            <div className={styles.headerTitles}>
              <h1 className={styles.pageTitle}>{pageTitle}</h1>
              {pageSubtitle && (
                <p className={styles.pageSubtitle}>{pageSubtitle}</p>
              )}
              {couponBreadcrumb.show && (
                <nav className={styles.pageBreadcrumb} aria-label="Coupon navigation">
                  <button
                    type="button"
                    className={styles.breadcrumbBack}
                    onClick={() => navigate(routes.coupons.list)}
                    aria-label="Back to coupons"
                  >
                    <ArrowBackIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className={styles.breadcrumbLink}
                    onClick={() => navigate(routes.coupons.list)}
                  >
                    Coupons
                  </button>
                  <ChevronRightIcon className={styles.breadcrumbSep} fontSize="small" />
                  <span className={styles.breadcrumbCurrent}>{couponBreadcrumb.label}</span>
                </nav>
              )}
            </div>
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userAvatar}>{userInitials}</span>
            <span className={styles.greeting}>Hi, {displayName}</span>
          </div>
        </header>
      </div>

      <div className={styles.body}>
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav}>
            {SIDEBAR_ITEMS.map(({ id, label, icon: Icon, path, match }) => (
              <button
                key={id}
                type="button"
                className={`${styles.navItem} ${
                  match(location.pathname) ? styles.navItemActive : ""
                }`}
                onClick={() => navigate(path)}
              >
                <Icon className={styles.navIcon} />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          <div className={styles.sidebarFooter}>
            <button
              type="button"
              className={styles.logoutItem}
              onClick={handleLogout}
            >
              <LogoutOutlinedIcon className={styles.navIcon} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main
          className={`${styles.mainContent} ${
            couponBreadcrumb.show ? styles.mainContentCouponForm : ""
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
